import { ReactElement, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button, Divider, Loader, Modal, Svg, Text } from '@hopskipdrive/react-components';
import { useQueryClient } from '@tanstack/react-query';

import { useGetSafetyIncident, useGetTripRelations } from '../../../hooks/useIncidentApi';
import { IncidentFollowUp } from '../../../types/incidents';
import { AccountBlockField } from '../fields/AccountBlockField';
import { AccountFieldStatic } from '../fields/AccountField';
import { ActionTakenField } from '../fields/ActionTakenField';
import { AdminNotesField } from '../fields/AdminNotesField';
import { AssigneeIdField } from '../fields/AssigneeIdField';
import { ClassificationIdField } from '../fields/ClassificationIdField';
import { DescriptionField } from '../fields/DescriptionField';
import { FollowupField } from '../fields/FollowupField';
import { ImpactedUserIdsField } from '../fields/ImpactedUserIdsField';
import { IncidentDateField } from '../fields/IncidentDateField';
import { IncidentReportField } from '../fields/IncidentReportField';
import { KustomerUrlField } from '../fields/KustomerUrlField';
import { OffenderIdField } from '../fields/OffenderIdField';
import { OffenderTypeField } from '../fields/OffenderTypeField';
import { ReportedOnField } from '../fields/ReportedOnField';
import { RiderBlockField } from '../fields/RiderBlockField';
import { StatusField } from '../fields/StatusField';
import { TripIdFieldStatic } from '../fields/TripIdField';
import {
  CloseButton,
  IncidentDetailsContainer,
  StyledDrawer,
  StyledForm,
  StyledFormLabel,
  StyledModalActions,
} from '../IncidentForm.styles';

import { editIncidentValidationSchema } from './validationSchema';

export interface EditIncidentFormValues {
  classificationId: number;
  subclassificationId: number | null;
  offenderId: number;
  offenderType: string;
  assigneeId: number | null;
  kustomerUrl: string;
  description: string;
  reportedOn: string | Date | null;
  incidentDate: string | Date | null;
  status: string;
  actionTaken: string;
  incidentReportFiled: boolean;
  followUps: IncidentFollowUp[];
  riderBlock: boolean;
  accountBlock: boolean;
  impactedUserIds: Array<number>;
}

interface EditIncidentFormProps {
  incidentId: number;
  isOpen: boolean;
  isSubmitting: boolean;
  isDeleting: boolean;
  onClose: () => void;
  onSubmit: (data: EditIncidentFormValues) => void;
  onDelete: () => void;
}

const EditIncidentForm = ({
  incidentId,
  isOpen,
  isSubmitting,
  isDeleting,
  onClose,
  onSubmit,
  onDelete,
}: EditIncidentFormProps): ReactElement => {
  const queryClient = useQueryClient();
  const {
    isFetching: isIncidentFetching,
    isError: isIncidentError,
    data: incidentResponse,
  } = useGetSafetyIncident(incidentId);
  const { isFetching: isTripFetching, data: tripResponse } = useGetTripRelations(incidentResponse?.trip?.id || null);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const methods = useForm<EditIncidentFormValues>({
    resolver: yupResolver(editIncidentValidationSchema),
  });

  const currentOffenderType = methods.watch('offenderType');

  useEffect(() => {
    if (!isOpen) {
      methods.reset();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isDeleting) {
      setOpenDeleteModal(false);
    }
  }, [isDeleting]);

  return (
    <>
      <StyledDrawer open={isOpen} anchor="right">
        <Modal.Title>
          <Box display="flex" justifyContent="space-between">
            <Text variant="h6">Edit T&S Incident Form</Text>
            <CloseButton onClick={onClose}>
              <Svg height="20px" icon="cross" />
            </CloseButton>
          </Box>
        </Modal.Title>
        <Modal.Content>
          {isIncidentFetching && <Loader />}
          {isIncidentError && (
            <Alert
              severity="error"
              variant="outlined"
              message={'Could not load incident. Please try again.'}
              action={
                <Button
                  color="primary"
                  size="small"
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['getSafetyIncident', incidentId] })}
                >
                  Try Reloading
                </Button>
              }
            />
          )}
          {!isIncidentFetching && incidentResponse && (
            <FormProvider {...methods}>
              <StyledForm id="edit-incident-form" onSubmit={methods.handleSubmit(onSubmit)}>
                <Text variant="h6" textColor="secondaryText" marginBottom="8px">
                  #{incidentResponse.id}
                </Text>
                <StatusField defaultValue={incidentResponse.status} />
                <ReportedOnField defaultValue={incidentResponse.reportedOn} />
                <IncidentDateField defaultValue={incidentResponse.incidentDate} />
                <DescriptionField defaultValue={incidentResponse.description} />
                <KustomerUrlField defaultValue={incidentResponse.kustomerUrl} />
                <AssigneeIdField defaultValue={incidentResponse.assignee} />
                <OffenderTypeField defaultValue={incidentResponse.offenderType} />
                <IncidentDetailsContainer>
                  <ClassificationIdField
                    defaultValue={{
                      classification: incidentResponse.classification,
                      subclassification: incidentResponse.subclassification,
                    }}
                  />
                  <TripIdFieldStatic defaultValue={incidentResponse.trip?.slug} />
                  <AccountFieldStatic defaultValue={incidentResponse.account} />
                  {isTripFetching && <Loader />}
                  {!isTripFetching && (currentOffenderType || incidentResponse.offenderType) && (
                    <>
                      <OffenderIdField
                        defaultValue={incidentResponse.offender?.id}
                        currentOffenderType={
                          currentOffenderType?.toLowerCase() || incidentResponse.offenderType?.toLowerCase()
                        }
                        drivers={tripResponse?.drivers || []}
                        riders={tripResponse?.riders || []}
                        carePartnerDrivers={tripResponse.carePartnerDrivers || []}
                        riderAssistants={tripResponse.riderAssistants || []}
                      />
                      <ImpactedUserIdsField
                        defaultValue={
                          incidentResponse.impactedUsers
                            ? incidentResponse.impactedUsers
                                .map((item) => item.id)
                                .filter((item) => typeof item === 'number')
                            : []
                        }
                        currentOffenderType={
                          currentOffenderType?.toLowerCase() || incidentResponse.offenderType?.toLowerCase()
                        }
                        drivers={tripResponse?.drivers || []}
                        riders={tripResponse?.riders || []}
                        carePartnerDrivers={tripResponse.carePartnerDrivers || []}
                        riderAssistants={tripResponse.riderAssistants || []}
                      />
                    </>
                  )}
                </IncidentDetailsContainer>
                <FollowupField defaultValue={incidentResponse.followUps} />
                <ActionTakenField defaultValue={incidentResponse.actionTaken} />
                <StyledFormLabel>Record any additional steps taken:</StyledFormLabel>
                <IncidentReportField defaultValue={incidentResponse.incidentReportFiled} />
                <RiderBlockField defaultValue={incidentResponse.riderBlock} />
                <AccountBlockField defaultValue={incidentResponse.accountBlock} />
                <AdminNotesField defaultValue={incidentResponse.adminNotes} incidentId={incidentResponse.id} />
              </StyledForm>
            </FormProvider>
          )}
          {/* TODO: Error if problem fetching incident */}
        </Modal.Content>

        {!isIncidentFetching && incidentResponse && (
          <StyledModalActions>
            <Divider />
            <Box className="buttons">
              <Button color="warning" onClick={() => setOpenDeleteModal(true)}>
                <Svg icon="trashcan" /> Delete
              </Button>
              <Button form="edit-incident-form" type="submit" color="primary" disabled={isSubmitting}>
                Save {isSubmitting && <Loader className="loader" size={12} />}
              </Button>
            </Box>
          </StyledModalActions>
        )}
      </StyledDrawer>

      <Modal open={openDeleteModal}>
        <Modal.Title>
          <Text variant="h6">Are you sure you want to delete this incident?</Text>
        </Modal.Title>
        <StyledModalActions>
          <Divider />
          <Box className="buttons">
            <Button variant="text" onClick={() => setOpenDeleteModal(false)}>
              Cancel
            </Button>
            <Button color="primary" onClick={onDelete} disabled={isDeleting}>
              OK {isDeleting && <Loader className="loader" size={12} />}
            </Button>
          </Box>
        </StyledModalActions>
      </Modal>
    </>
  );
};

export default EditIncidentForm;
